import type {
  ISupplierProduct,
  ISupplierProductWithItemsPropertyPopulated,
} from '~/types/supplierProduct/models'
import type { ISupplierProductItem } from '~/types/supplierProductItem/models'
import type { IEntitySelectItem } from '~/types/models'
import { isSetKitProduct } from '~/types/product/models'

const mn = '[cynqueModules/dataObjects/SupplierProduct.ts]'

export function SupplierProduct(o: ISupplierProduct): ISupplierProduct {
  const fn = '[cynqueModules/dataObjects/SupplierProduct.ts] SupplierProduct |'
  if (!o) {
    throw new Error(`${fn} SupplierProduct called with falsey supplierProduct`)
  }
  if (!o.product) {
    throw new Error(
      `${fn} SupplierProduct called with falsey supplierProduct.product`
    )
  }
  const obj = { ...o }
  obj.label = getSupplierProductLabel(obj)
  return obj
}

// TEMP TESTING SHIM — REMOVE once the server stops denormalizing productGroup
// onto supplier-products (canonical value lives at product.productGroup).
// Strips any root-level productGroup off a retrieved supplier-product and
// recurses into nested items[].childSupplierProduct sub-trees. Non-mutating.
export function stripRootLevelProductGroup<T extends ISupplierProduct>(
  supplierProduct: T
): T {
  if (!supplierProduct) {
    return supplierProduct
  }
  const { productGroup, ...rest } = supplierProduct
  const stripped = rest as T
  const withItems = stripped as ISupplierProduct & {
    items?: ISupplierProductItem[]
  }
  if (Array.isArray(withItems.items)) {
    withItems.items = withItems.items.map((item) =>
      item?.childSupplierProduct
        ? {
            ...item,
            childSupplierProduct: stripRootLevelProductGroup(
              item.childSupplierProduct
            ),
          }
        : item
    )
  }
  return stripped
}

export function getSupplierProductLabel(
  supplierProduct: ISupplierProduct | undefined
) {
  if (!supplierProduct) {
    return undefined
  }
  return supplierProduct.descr ?? supplierProduct.product.descr
}

export function getSupplierProductEntitySelectItemFromSupplierProduct(
  supplierProduct: ISupplierProduct | undefined,
  parent?: IEntitySelectItem
) {
  if (!supplierProduct) {
    return undefined
  }
  return {
    id: supplierProduct.id,
    label: getSupplierProductLabel(supplierProduct),
    ...(parent && { parent }),
  }
}

/**
 * Which supplier-product items the walk descends through.
 *
 * The default -- descend into anything that HAS a child supplier-product -- is
 * what the create-time options list wants: one dialog covering the whole
 * product tree.
 *
 * The "set this kit's options" dialog passes a narrower one. There, each
 * nested plain kit is its own msService with its own row and its own options
 * dialog, so descending into it from the parent would offer the same options in
 * two places. Nested SETS are different: a Set's header service is never
 * materialized (doExcludeKitServiceWhenBuildingServices), so there is no row to
 * hang a dialog on and its alternatives have to surface here or nowhere. So is
 * anything inside an OPTION: what comes with it has to follow it -- see
 * `doRecurseIntoOptionScope` in msServiceOptions.ts.
 *
 * `parentOptionUid` is the nearest option enclosing the item, if any.
 */
export type TDoRecurseIntoSupplierProductItem = (
  supplierProductItem: ISupplierProductItem,
  context: { parentOptionUid?: string }
) => boolean

const doRecurseIntoAnyChildSupplierProduct: TDoRecurseIntoSupplierProductItem =
  (supplierProductItem) => !!supplierProductItem.childSupplierProduct

/**
 * Which supplier-product items the walk RETURNS. The default -- the optional
 * ones -- is what every options list wants. `collectMandatoryItemsUnderOptions`
 * passes another, and gets the same `_selectUid`s and `_parentOptionUid`s for
 * free because it is the same walk.
 */
type TDoCollectSupplierProductItem = (
  supplierProductItem: ISupplierProductItem
) => boolean

const doCollectOptionalItem: TDoCollectSupplierProductItem = (
  supplierProductItem
) => !!supplierProductItem.isOptional

export function collectOptionalItemsWithContext({
  supplierProduct,
  parentSupplierProductItemPath,
  triggeringSupplierProductItem,
  parentOptionUid,
  enclosingKitUid,
  doRecurseIntoItem = doRecurseIntoAnyChildSupplierProduct,
  doCollectItem = doCollectOptionalItem,
}: {
  supplierProduct: ISupplierProduct | ISupplierProductWithItemsPropertyPopulated
  parentSupplierProductItemPath?: string
  triggeringSupplierProductItem?: ISupplierProductItem
  // The nearest enclosing OPTION, when the walk is inside one -- see below.
  parentOptionUid?: string
  // The nearest enclosing nested plain KIT, when the walk is inside one -- see
  // `_enclosingKitUid` below.
  enclosingKitUid?: string
  doRecurseIntoItem?: TDoRecurseIntoSupplierProductItem
  doCollectItem?: TDoCollectSupplierProductItem
}): Array<
  ISupplierProductItem & {
    _selectUid: string
    _parentOptionUid?: string
    _enclosingKitUid?: string
    parentSupplierProductItem?: ISupplierProductItem
  }
> {
  // ⚠ ONE WALK IN seqNum ORDER, not two blocks.
  //
  // This used to build `[...optionalItems, ...childItems]` — every one of the
  // supplier-product's OWN optional items, then everything reached by recursion,
  // each group in whatever order the API happened to return. So a kit's own
  // add-ons all sorted ahead of a nested Set's alternatives no matter which slot
  // the Set occupied, and `seqNum` was never consulted at all.
  //
  // Walking once, in seqNum order, and emitting each item's own entry followed
  // immediately by whatever recursing into it yields, puts every option where the
  // supplier-product says it belongs. The SET of entries is unchanged — an item
  // that is both optional AND has a child still contributes both, in that order —
  // so `_selectUid`s, section keys and the divider logic that reads them all
  // behave exactly as before.
  const orderedItems = sortSupplierProductItemsBySeqNum(supplierProduct.items)

  const collected: Array<
    ISupplierProductItem & {
      _selectUid: string
      _parentOptionUid?: string
      _enclosingKitUid?: string
      parentSupplierProductItem?: ISupplierProductItem
    }
  > = []

  // ⚠ A SET INSIDE A SET. An optional item can itself lead to a Set: it is one
  // alternative of the outer Set, and the inner Set's own alternatives only
  // apply when it is the one taken. So every option found by recursing into
  // an optional item records that item's uid as `_parentOptionUid` -- the
  // inner choice depends on it (cynqueModules/optionSections.ts). Recursing
  // into a NON-optional item (a kit's always-present link to a Set) passes the
  // enclosing option through unchanged, which is `undefined` at the top.
  //
  // ⚠ `_enclosingKitUid` IS WHERE A ROW HANGS. A nested plain Kit has a row of
  // its own, and everything built inside it hangs off that row; a Set has none,
  // so its members hang off the kit above it. Every entry records the uid of
  // the nearest nested plain Kit it sits in, `undefined` meaning the walk's own
  // kit -- which is how the options dialog finds the parent row for an option
  // it builds, and scopes the search for a nested Kit's own row.
  for (const item of orderedItems) {
    const selectUid = `${item.id}__${parentSupplierProductItemPath || 'root'}`
    if (doCollectItem(item)) {
      collected.push({
        ...item,
        supplierProduct,
        _selectUid: selectUid,
        ...(parentOptionUid && { _parentOptionUid: parentOptionUid }),
        ...(enclosingKitUid && { _enclosingKitUid: enclosingKitUid }),
        ...(triggeringSupplierProductItem && {
          parentSupplierProductItem: triggeringSupplierProductItem,
        }),
      })
    }
    if (
      item.childSupplierProduct &&
      doRecurseIntoItem(item, { parentOptionUid })
    ) {
      collected.push(
        ...collectOptionalItemsWithContext({
          supplierProduct: item.childSupplierProduct,
          parentSupplierProductItemPath: parentSupplierProductItemPath
            ? `${parentSupplierProductItemPath}>${item.id}`
            : item.id,
          triggeringSupplierProductItem: item,
          parentOptionUid: item.isOptional ? selectUid : parentOptionUid,
          enclosingKitUid: isSetKitProduct(item.product)
            ? enclosingKitUid
            : selectUid,
          doRecurseIntoItem,
          doCollectItem,
        })
      )
    }
  }

  return collected
}

/**
 * The MANDATORY items that sit inside an option: the members an optional Set
 * marks mandatory, and everything mandatory inside an optional plain Kit --
 * item-products and nested plain Kits alike.
 *
 * Taking the option builds these, whatever is picked inside it, and leaving it
 * out builds none of them (KitService's buildServicesForKitService). For a Set,
 * which has no row of its own, they are the whole trace of its being taken.
 * Each carries the nearest enclosing option as `_parentOptionUid`.
 *
 * Mandatory items no option encloses are left out: they are always there and
 * nobody's choice. So are Sets: a mandatory Set builds nothing of its own, and
 * the walk reaches its members anyway. A mandatory plain Kit is NOT left out --
 * it has a row, and that row has to follow the option like any other.
 */
export function collectMandatoryItemsUnderOptions({
  supplierProduct,
  doRecurseIntoItem,
}: {
  supplierProduct: ISupplierProduct | ISupplierProductWithItemsPropertyPopulated
  doRecurseIntoItem?: TDoRecurseIntoSupplierProductItem
}) {
  return collectOptionalItemsWithContext({
    supplierProduct,
    doRecurseIntoItem,
    doCollectItem: (supplierProductItem) =>
      !supplierProductItem.isOptional &&
      !isSetKitProduct(supplierProductItem.product),
  }).filter((item) => !!item._parentOptionUid)
}

/**
 * A supplier-product's items in `seqNum` order, arrival order breaking ties.
 *
 * Copies rather than sorting in place: `supplierProduct.items` is live data the
 * caller still owns, and the create flow reads it again afterwards.
 *
 * ⚠ An item with no usable `seqNum` sorts LAST rather than first, and ties fall
 * back to arrival index — so a supplier-product whose items all lack one keeps
 * exactly the order the API sent, which is what makes adopting this safe on data
 * that predates anyone populating the field.
 */
export function sortSupplierProductItemsBySeqNum(
  items: ISupplierProductItem[] | undefined | null
): ISupplierProductItem[] {
  if (!items?.length) {
    return []
  }
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const aSeq =
        typeof a.item.seqNum === 'number'
          ? a.item.seqNum
          : Number.MAX_SAFE_INTEGER
      const bSeq =
        typeof b.item.seqNum === 'number'
          ? b.item.seqNum
          : Number.MAX_SAFE_INTEGER
      return aSeq === bSeq ? a.index - b.index : aSeq - bSeq
    })
    .map(({ item }) => item)
}

export function getSupplierProductsForSupplierProducts({
  supplierProducts,
}: {
  supplierProducts: ISupplierProduct[]
}) {
  const fn = `${mn} getSupplierProductsForSupplierProducts |`
  const aggregatedSupplierProductsForParentSupplierProducts: ISupplierProduct[] =
    []

  for (const supplierProduct of supplierProducts) {
    aggregatedSupplierProductsForParentSupplierProducts.push(
      ...getSupplierProductsForSupplierProduct({ supplierProduct })
    )
  }
  console.debug(
    `${fn} aggregatedSupplierProductsForParentSupplierProducts...`,
    aggregatedSupplierProductsForParentSupplierProducts
  )
  return aggregatedSupplierProductsForParentSupplierProducts
}

export function getSupplierProductsForSupplierProduct({
  supplierProduct,
}: {
  supplierProduct: ISupplierProduct
}) {
  const fn = `${mn} getSupplierProductsForSupplierProduct |`
  let aggregatedSupplierProductsForParentSupplierProduct: ISupplierProduct[] =
    []
  aggregatedSupplierProductsForParentSupplierProduct =
    _getSupplierProductsForSupplierProduct({
      supplierProduct,
    })
  console.debug(
    `${fn} aggregatedSupplierProductsForParentSupplierProduct:`,
    aggregatedSupplierProductsForParentSupplierProduct
  )
  return aggregatedSupplierProductsForParentSupplierProduct
}

function _getSupplierProductsForSupplierProduct({
  supplierProduct,
}: {
  supplierProduct: ISupplierProduct
}): ISupplierProduct[] {
  const fn = `${mn} _getSupplierProductsForSupplierProduct |`
  const theseSupplierProductsForSupplierProductItems: ISupplierProduct[] = [
    supplierProduct,
  ]

  const supplierProductItemsWithChildSupplierProducts =
    supplierProduct.items?.filter(
      (supplierProductItem: ISupplierProductItem) =>
        supplierProductItem.childSupplierProduct
    )
  if (!supplierProductItemsWithChildSupplierProducts) {
    return theseSupplierProductsForSupplierProductItems
  }

  for (const supplierProductItem of supplierProductItemsWithChildSupplierProducts) {
    theseSupplierProductsForSupplierProductItems.push(
      ..._getSupplierProductsForSupplierProduct({
        supplierProduct: supplierProductItem.childSupplierProduct,
      })
    )
  }

  console.debug(`${fn} theseSupplierProductsForSupplierProductItems...`)
  console.dir(theseSupplierProductsForSupplierProductItems)
  return theseSupplierProductsForSupplierProductItems
}
